/**
 * Contract commands (spec §9.3, plan §6 M2-08): accept, abandon, hand in and expire. Pure Career commands that
 * return a `CmdResult` and never throw for a refusal.
 *
 * - Accepting copies a board offer into `career.contracts.active` with `deadlineAt = gameTime + duration`. A
 *   courier needs `podMassT` free and loads a sealed pod `CargoComponent` tagged with the contract id.
 * - Handing in pays the reward and the offer's standing (+5..+10) with the issuing station's faction. Delivery:
 *   the tonnes leave the hold at the issuing station. Recovery: the named component (tagged with the contract
 *   id) leaves the hold at the issuing station. Courier: the pod leaves the hold at the destination. Clear
 *   raiders: at the issuing station once `progress.onGroupDefeated` marked it `ready`. The Reach types (lane repair,
 *   relay parts, chart a system; M3 R22g): at the issuing station once a Reach event marked them `ready`.
 * - Abandoning and expiring (at `deadlineAt` exactly) cost `standingPenalty`, drop a courier pod and untag a
 *   recovered component (it becomes ordinary salvage). Both report `contractFailed`, which the career runtime
 *   also uses to remove the job's wreck or raider group.
 *
 * Completed and failed ids are kept for the current and the previous board cycle only: older ids can never
 * match a board again (the cycle is part of the id), so the save stays small.
 *
 * M8 works delivery (SNK-6b2, D-TBD T31): a delivery with `params.siteId` carries a non-raw good (`params.good`,
 * `cargo.parts`) into a strategic works site. Its hand-in is a treasury transfer priced by the worker: the runtime
 * sends `worksDeliver` (`worksHandIn` builds it) and, once the worker settled, `bookWorksHandIn` removes exactly the
 * tonnes the site accepted and credits exactly the `paid` the treasury paid. `contract.reward` (the board's estimate)
 * is never paid, so `handInContract` refuses a works contract, and no `unlockMult` applies.
 *
 * M7 §12.1 logging (S1): a hand-in books its reward to the Career's income mix (`story/logging.ts`: clear raiders →
 * `combat`, every other type → `contracts`). M7 S12: a Holdfast hand-in counts toward the guaranteed Watch logs
 * (`story/m12.ts holdfastContractDone`).
 *
 * Career layer: pure, not per-frame.
 */
import { stationDef, stationIndexOf } from '../data/m2'
import type { M2Data, StationId } from '../data/m2Schemas'
import { EPSILON, GOOD_NAMES, cargoFreeT, clampStanding, withComponent, withGood, withWallet, withWare, withoutComponent } from '../career/ops'
import { containerHoldT } from '../career/hullFit'
import { goodT, isStrategicGoodId, type StrategicGoodId } from '../career/parts'
import { cargoFull, fail, formatTonnes, ok, type CareerEffect, type CmdResult, type CmdSuccess, type CommandError } from '../career/result'
import type { CargoComponent, Career, ContractState } from '../career/types'
import { contractIncomeSource, recordIncome } from '../story/logging'
import { holdfastContractDone } from '../story/m12'
import { findOffer } from './board'
import { boardCycle, type ContractOffer } from './generate'

function stationName(data: M2Data, id: StationId): string {
  return stationIndexOf(data, id) >= 0 ? stationDef(data, id).name : id
}

function unavailable(id: string): CommandError {
  return { code: 'contractUnavailable', message: `Contract ${id} is not available.`, detail: { id } }
}

function requirement(message: string, id: string): CommandError {
  return { code: 'contractRequirement', message, detail: { id } }
}

/** The lease cycle encoded in a contract id (`${station}:${cycle}:${k}`), or -1. */
export function contractCycle(id: string): number {
  const parts = id.split(':')
  if (parts.length < 3) return -1
  const n = Number(parts[parts.length - 2])
  return Number.isInteger(n) ? n : -1
}

/** Drops completed or failed ids older than the previous board cycle. */
function pruneIds(ids: readonly string[], cycle: number): string[] {
  return ids.filter((id) => contractCycle(id) >= cycle - 1)
}

/** Adds `delta` standing with `station`'s faction and reports the change. */
function applyStanding(data: M2Data, career: Career, station: StationId, delta: number, effects: CareerEffect[]): Career {
  const faction = stationDef(data, station).faction
  const before = career.standings[faction]
  const value = clampStanding(before + delta)
  if (value !== before) effects.push({ kind: 'standingChanged', faction, delta: value - before, value })
  return { ...career, standings: { ...career.standings, [faction]: value } }
}

/** Accepts offer `offerId` from `stationId`'s current board. */
export function acceptContract(data: M2Data, career: Career, stationId: StationId, offerId: string): CmdResult<Career> {
  if (stationIndexOf(data, stationId) < 0 || !stationDef(data, stationId).services.includes('contracts')) {
    return fail('serviceUnavailable', `${stationName(data, stationId)} has no contract board.`, { station: stationId })
  }
  const offer: ContractOffer | null = findOffer(data, career, stationId, offerId)
  if (offer === null) return { ok: false, error: unavailable(offerId) }
  let next = career
  if (offer.type === 'courier') {
    const podMassT = data.contracts.types.courier.podMassT
    // M10 P5a6b: the pod rides as `container`, so it takes `podMassT ÷ holdMult.container` of the weighted free hold.
    const needT = containerHoldT(career.ship.defId, podMassT)
    const free = cargoFreeT(career)
    if (free + EPSILON < needT) return cargoFull(needT, free)
    const pod: CargoComponent = {
      id: offer.params.podId ?? `${offer.id}:pod`,
      type: 'cargoPod',
      condition: 1,
      massT: podMassT,
      value: 0,
      contractId: offer.id,
      sealed: true,
    }
    next = withComponent(next, pod)
  }
  const state: ContractState = {
    id: offer.id,
    type: offer.type,
    station: offer.station,
    issuedCycle: offer.cycle,
    acceptedAt: career.gameTime,
    deadlineAt: career.gameTime + offer.durationSeconds,
    reward: offer.reward,
    standing: offer.standing,
    premium: offer.premium,
    params: offer.params,
    status: 'active',
  }
  return ok({ ...next, contracts: { ...next.contracts, active: [...next.contracts.active, state] } })
}

/** Removes a failed job's contract cargo: the courier pod leaves, a recovered component loses its tag. */
function dropContractCargo(career: Career, contract: ContractState): Career {
  let next = career
  if (contract.type === 'courier') {
    for (const c of career.cargo.components) if (c.contractId === contract.id) next = withoutComponent(next, c.id)
  } else if (next.cargo.components.some((c) => c.contractId === contract.id)) {
    const components = next.cargo.components.map((c) => {
      if (c.contractId !== contract.id) return c
      const untagged: CargoComponent = { ...c }
      delete untagged.contractId
      return untagged
    })
    next = { ...next, cargo: { ...next.cargo, components } }
  }
  return next
}

/** Fails `contract`: penalty, cargo, the failed list and the effects. */
function failContract(data: M2Data, career: Career, contract: ContractState, effects: CareerEffect[]): Career {
  let next = dropContractCargo(career, contract)
  const penalty = data.contracts.standingPenalty
  next = applyStanding(data, next, contract.station, -penalty, effects)
  const cycle = boardCycle(next)
  next = {
    ...next,
    contracts: {
      active: next.contracts.active.filter((c) => c.id !== contract.id),
      completedIds: pruneIds(next.contracts.completedIds, cycle),
      failedIds: [...pruneIds(next.contracts.failedIds, cycle), contract.id],
    },
  }
  effects.push({ kind: 'contractFailed', contractId: contract.id, standing: -penalty })
  return next
}

/** Abandons accepted contract `contractId` (−`standingPenalty` with the issuer, the courier pod is removed). */
export function abandonContract(data: M2Data, career: Career, contractId: string): CmdResult<Career> {
  const contract = career.contracts.active.find((c) => c.id === contractId)
  if (contract === undefined) return { ok: false, error: unavailable(contractId) }
  const effects: CareerEffect[] = []
  return ok(failContract(data, career, contract, effects), effects)
}

/** The hold component that satisfies `contract` (recovery target or courier pod), if any. */
function contractComponent(career: Career, contract: ContractState): CargoComponent | undefined {
  return career.cargo.components.find(
    (c) => c.contractId === contract.id && (contract.type !== 'recovery' || c.type === contract.params.componentType),
  )
}

/** True for a works delivery (M8 SNK-6b2): paid by the site owner's treasury through the worker, never `reward`. */
export function isWorksContract(contract: ContractState): boolean {
  return contract.type === 'delivery' && contract.params.siteId !== undefined
}

/** The hold check of a works delivery: `needT` t of its good in `cargo.parts`. */
function worksHoldCheck(career: Career, contract: ContractState, needT: number): CommandError | null {
  const good = contract.params.good
  if (good === undefined) return unavailable(contract.id)
  const have = goodT(career.cargo.parts, good)
  if (have + EPSILON < needT) return requirement(`Deliver ${formatTonnes(needT)} t of ${GOOD_NAMES[good]}: ${formatTonnes(have)} t in the hold.`, contract.id)
  return null
}

/**
 * Tonnes a works contract hands in now (M8 SNK-6b2): its tonnes held to what the site still misses (`live`), or its
 * tonnes without a live row (the worker then decides). The hold must cover this; the plain contract tonnes are not
 * required once AI deliveries shrank the site's need.
 */
export function worksNeedT(contract: ContractState, live: WorksLive | null): number {
  const tons = contract.params.tons ?? 0
  return live === null ? tons : Math.min(tons, live.qty)
}

/**
 * Why `contractId` cannot be handed in at `stationId` right now, or null when it can. A works delivery checks the hold
 * against what the site still needs (`live`, the local view's row: `worksLive`), the same rule as `worksHandIn`.
 */
export function handInCheck(data: M2Data, career: Career, stationId: StationId, contractId: string, live: WorksLive | null = null): CommandError | null {
  const contract = career.contracts.active.find((c) => c.id === contractId)
  if (contract === undefined) return unavailable(contractId)
  const at = contract.type === 'courier' ? (contract.params.destination ?? contract.station) : contract.station
  if (stationId !== at) return requirement(`Hand this contract in at ${stationName(data, at)}.`, contractId)
  switch (contract.type) {
    case 'delivery': {
      if (isWorksContract(contract)) {
        const need = worksNeedT(contract, live)
        const good = contract.params.good
        if (!(need > EPSILON) && good !== undefined) return requirement(`The site no longer needs ${GOOD_NAMES[good]}.`, contractId)
        return worksHoldCheck(career, contract, need)
      }
      const ware = contract.params.ware
      const tons = contract.params.tons ?? 0
      if (ware === undefined) return unavailable(contractId)
      const have = career.cargo.wares[ware]
      if (have + EPSILON < tons) {
        return requirement(`Deliver ${formatTonnes(tons)} t of ${data.wares.wares.find((w) => w.id === ware)?.name ?? ware}: ${formatTonnes(have)} t in the hold.`, contractId)
      }
      return null
    }
    case 'recovery':
      return contractComponent(career, contract) === undefined ? requirement('The contract component is not in the hold.', contractId) : null
    case 'courier':
      return contractComponent(career, contract) === undefined ? requirement('The sealed pod is not in the hold.', contractId) : null
    case 'clearRaiders':
      return contract.status === 'ready' ? null : requirement('The raider group has not been destroyed yet.', contractId)
    // M3 bridge (R22g): the Reach event marked the job ready (`progress.ts`); nothing leaves the hold.
    case 'laneRepair':
      return contract.status === 'ready' ? null : requirement('The lane ring has not been repaired yet.', contractId)
    case 'relayParts':
      return contract.status === 'ready' ? null : requirement('The parts have not been delivered to the relay yet.', contractId)
    case 'chartSystem':
      return contract.status === 'ready' ? null : requirement('The system has not been charted yet.', contractId)
  }
}

/** Hands in `contractId` at `stationId`: removes the goods, pays the reward and the standing. */
export function handInContract(data: M2Data, career: Career, stationId: StationId, contractId: string): CmdResult<Career> {
  const error = handInCheck(data, career, stationId, contractId)
  if (error !== null) return { ok: false, error }
  const contract = career.contracts.active.find((c) => c.id === contractId) as ContractState
  // A works delivery is paid by the faction treasury through the worker (`bookWorksHandIn`), never `reward`.
  if (isWorksContract(contract)) return { ok: false, error: requirement(WORKS_VIA_WORKER, contractId) }
  let next = career
  if (contract.type === 'delivery' && contract.params.ware !== undefined) {
    next = withWare(next, contract.params.ware, -(contract.params.tons ?? 0))
  } else if (contract.type === 'recovery' || contract.type === 'courier') {
    const item = contractComponent(next, contract)
    if (item !== undefined) next = withoutComponent(next, item.id)
  }
  const [paid, effects] = completed(data, next, contract, contract.reward)
  return ok(paid, effects)
}

/** Pays `reward` and the standing for `contract` and moves it to the completed list (the shared hand-in tail). */
function completed(data: M2Data, career: Career, contract: ContractState, reward: number): [Career, CareerEffect[]] {
  const contractId = contract.id
  const effects: CareerEffect[] = []
  let next = withWallet(career, reward)
  next = recordIncome(next, contractIncomeSource(contract.type), reward)
  next = applyStanding(data, next, contract.station, contract.standing, effects)
  // M7 S12 (spec §5 "before Watchers"): the 1st and 3rd Holdfast hand-ins drop a guaranteed Watch log.
  if (stationDef(data, contract.station).faction === 'holdfast') next = holdfastContractDone(next)
  const cycle = boardCycle(next)
  next = {
    ...next,
    contracts: {
      active: next.contracts.active.filter((c) => c.id !== contractId),
      completedIds: [...pruneIds(next.contracts.completedIds, cycle), contractId],
      failedIds: pruneIds(next.contracts.failedIds, cycle),
    },
    stats: { ...next.stats, earned: next.stats.earned + reward },
  }
  effects.unshift({
    kind: 'contractCompleted',
    contractId,
    reward,
    standing: contract.standing,
    type: contract.type,
    station: contract.station,
  })
  return [next, effects]
}

/** The refusal of a works contract outside the worker path. */
const WORKS_VIA_WORKER = 'This works delivery is paid by the faction treasury: hand it in while the market is up.'

/** The live works row a hand-in is priced from (M8 SNK-6b2). */
export interface WorksLive {
  /** Tonnes the site still misses of the contract's good (the view row's `qty`). */
  qty: number
  /** Credits per t today: the row's premium × unit. */
  paidT: number
}

/** What `worksLive` reads of a system view (`SystemEconomyView`: its id and its request rows). */
export interface WorksView {
  readonly systemId: string
  readonly requests: readonly { readonly siteId?: string; readonly ware?: string; readonly qty?: number; readonly unit?: number; readonly premium?: number }[]
}

/**
 * The live works row of `contract` (the view's request row for its site and good): what the site still misses and
 * today's credits per t. Null when the view is another system's or has no such row; the worker then decides. Shared
 * by the runtime's hand-in and the Contracts screen, so the button and the hand-in agree.
 */
export function worksLive(view: WorksView | null | undefined, contract: ContractState): WorksLive | null {
  const { siteId, good, systemId } = contract.params
  if (view === null || view === undefined || siteId === undefined || view.systemId !== systemId) return null
  const row = view.requests.find((r) => r.siteId === siteId && r.ware === good)
  if (row === undefined || row.unit === undefined || row.premium === undefined) return null
  return { qty: row.qty ?? 0, paidT: row.premium * row.unit }
}

/** The `worksDeliver` a works hand-in sends (M8 SNK-6b2). */
export interface WorksHandIn {
  siteId: string
  good: StrategicGoodId
  /** Tonnes sent: the contract's tonnes, held to what the site still misses. */
  qty: number
  /** The client's price for `qty` (the worker checks it within its 2% tolerance and pays its own). */
  expectedReward: number
}

/**
 * The `worksDeliver` for works contract `contractId` handed in at `stationId` (M8 SNK-6b2): the contract's tonnes held
 * to what the site still misses (`live`), priced at the live row's credits per t (the board's per-tonne estimate
 * without one). Refused when it isn't a works contract handed in here, the site needs none of it now, or the hold is
 * short.
 */
export function worksHandIn(data: M2Data, career: Career, stationId: StationId, contractId: string, live: WorksLive | null): CmdResult<WorksHandIn> {
  const contract = career.contracts.active.find((c) => c.id === contractId)
  if (contract === undefined || !isWorksContract(contract)) return { ok: false, error: unavailable(contractId) }
  if (stationId !== contract.station) return { ok: false, error: requirement(`Hand this contract in at ${stationName(data, contract.station)}.`, contractId) }
  const { good, siteId } = contract.params
  const tons = contract.params.tons ?? 0
  if (good === undefined || siteId === undefined || !isStrategicGoodId(good) || !(tons > 0)) return { ok: false, error: unavailable(contractId) }
  const need = worksNeedT(contract, live)
  if (!(need > EPSILON)) return { ok: false, error: requirement(`The site no longer needs ${GOOD_NAMES[good]}.`, contractId) }
  const short = worksHoldCheck(career, contract, need)
  if (short !== null) return { ok: false, error: short }
  // The hold covers `need` here, so this is `need`: never more than the hold or the site's live need.
  const held = goodT(career.cargo.parts, good)
  const qty = held < need ? held : need
  const paidT = live === null ? contract.reward / tons : live.paidT
  return ok({ siteId, good, qty, expectedReward: paidT * qty })
}

/**
 * Books a works hand-in the worker settled (M8 SNK-6b2): exactly `accepted` t of the good leave the hold (they went
 * into the site) and the wallet gets exactly `paid`, what the owner's treasury paid (no `unlockMult`, never
 * `contract.reward`); the standing and the completion as any hand-in.
 */
export function bookWorksHandIn(data: M2Data, career: Career, contractId: string, outcome: { readonly accepted: number; readonly paid: number }): CmdResult<Career> {
  const contract = career.contracts.active.find((c) => c.id === contractId)
  if (contract === undefined || !isWorksContract(contract) || contract.params.good === undefined) return { ok: false, error: unavailable(contractId) }
  if (!(Number.isFinite(outcome.accepted) && outcome.accepted > 0 && Number.isFinite(outcome.paid) && outcome.paid >= 0)) {
    return fail('invalidAmount', `Invalid works hand-in: ${String(outcome.accepted)} t for ${String(outcome.paid)} cr.`, { id: contractId })
  }
  const short = worksHoldCheck(career, contract, outcome.accepted)
  if (short !== null) return { ok: false, error: short }
  const [next, effects] = completed(data, withGood(career, contract.params.good, -outcome.accepted), contract, outcome.paid)
  return ok(next, effects)
}

/**
 * Fails every accepted contract whose deadline has come (`deadlineAt <= now`), in accept order. Always
 * succeeds; the same career comes back when nothing expired.
 */
export function expireContracts(data: M2Data, career: Career, now: number): CmdSuccess<Career> {
  const due = career.contracts.active.filter((c) => c.deadlineAt <= now)
  if (due.length === 0) return ok(career)
  const effects: CareerEffect[] = []
  let next = career
  for (const c of due) next = failContract(data, next, c, effects)
  return ok(next, effects)
}

